import type { Category } from "@/services/collection/categories";
import { CategoryId } from "@/services/collection/categories";
import { createPageTitle } from "@/services/metaTags";
import type { MaybeRef } from "vue";
import { unref } from "vue";
import { useGettext } from "vue3-gettext";

export function useMarketplaceTexts(category: MaybeRef<Category | undefined>) {
  const { $gettext } = useGettext();

  const categoryId = computed(() => {
    const c = unref(category);
    if (c === undefined) {
      return undefined; // Category not loaded yet
    } else {
      return c.id;
    }
  });

  /** Can contain HTML! */
  const shortDescription = computed(() => {
    switch (categoryId.value) {
      case CategoryId.SportsCards:
        return $gettext("Hokej, fotbal, MMA, motorsport a&nbsp;mnoho dalších kategorií na jednom místě...");
      case CategoryId.Pokemon:
        return $gettext("Od kusovek až po elite trainer boxy, to vše nabízí naše specializované tržiště...");
      default:
        return undefined;
    }
  });

  const description = computed(() => {
    switch (categoryId.value) {
      case CategoryId.LegoSets:
        return $gettext("V naší sekci LEGO sety najdeš širokou nabídku stavebnic od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš limitované edice, retro sety nebo nejnovější témata, u nás snadno porovnáš nabídky a vybereš tu správnou LEGO stavebnici.");
      case CategoryId.Watches:
        return $gettext("V naší sekci hodinky najdeš širokou nabídku luxusních i vintage modelů od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš klasické mechanické hodinky nebo moderní limitované edice, u nás snadno porovnáš nabídky a vybereš ten správný časoměřič.");
      case CategoryId.Alcohol:
        return $gettext("V naší sekci alkohol najdeš širokou nabídku prémiových destilátů a vín od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš vzácné whisky, ročníková vína nebo limitované edice, u nás snadno porovnáš nabídky a vybereš ten správný sběratelský alkohol.");
      case CategoryId.Shoes:
        return $gettext("V naší sekci boty najdeš širokou nabídku sběratelských tenisek od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš limitované sneakers, retro modely nebo nejnovější spolupráce, u nás snadno porovnáš nabídky a vybereš ten správný pár.");
      case CategoryId.Art:
        return $gettext("V naší sekci umění najdeš širokou nabídku obrazů, soch a grafik od ověřených sběratelů a galerií přehledně na jednom místě. Ať už hledáš moderní umění, klasická díla nebo limitované tisky, u nás snadno porovnáš nabídky a vybereš to správné umělecké dílo.");
      case CategoryId.Furniture:
        return $gettext("V naší sekci nábytek najdeš širokou nabídku designového a starožitného nábytku od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš vintage kousky, autorský design nebo historický nábytek, u nás snadno porovnáš nabídky a vybereš ten správný kus.");
      case CategoryId.Jewellery:
        return $gettext("V naší sekci šperky najdeš širokou nabídku sběratelských klenotů od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš vintage šperky, luxusní hodinky nebo unikátní autorské kousky, u nás snadno porovnáš nabídky a vybereš ten správný šperk.");
      case CategoryId.Coins:
        return $gettext("V naší sekci mince najdeš širokou nabídku numismatických položek od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš historické mince, pamětní ražby nebo investiční zlato, u nás snadno porovnáš nabídky a vybereš tu správnou minci.");
      case CategoryId.ScaleModels:
        return $gettext("V naší sekci modely najdeš širokou nabídku zmenšených modelů od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš modely aut, letadel nebo lodí, u nás snadno porovnáš nabídky a vybereš ten správný model do tvé sbírky.");
      case CategoryId.Stamps:
        return $gettext("V naší sekci známky najdeš širokou nabídku filatelistických položek od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš vzácné známky, celiny nebo tematické série, u nás snadno porovnáš nabídky a vybereš tu správnou známku.");
      case CategoryId.FunkoPop:
        return $gettext("V naší sekci Funko Pop najdeš širokou nabídku sběratelských figurek od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš chase varianty, exclusive edice nebo klasické postavy, u nás snadno porovnáš nabídky a vybereš tu správnou figurku.");
      case CategoryId.VinylRecords:
        return $gettext("V naší sekci vinyly najdeš širokou nabídku gramofonových desek od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš vzácné první vydání, limitované edice nebo klasická alba, u nás snadno porovnáš nabídky a vybereš tu správnou desku.");
      case CategoryId.MusicalInstruments:
        return $gettext("V naší sekci hudební nástroje najdeš širokou nabídku nástrojů od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš vintage kytary, klasické klavíry nebo vzácné nástroje, u nás snadno porovnáš nabídky a vybereš ten správný nástroj.");
      case CategoryId.Autographs:
        return $gettext("V naší sekci autogramy najdeš širokou nabídku podepsaných předmětů od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš podpisy sportovců, celebrit nebo historických osobností, u nás snadno porovnáš nabídky a vybereš ten správný autogram.");
      case CategoryId.Perfumes:
        return $gettext("V naší sekci parfémy najdeš širokou nabídku sběratelských vůní od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš vzácné vintage parfémy, limitované edice nebo luxusní značky, u nás snadno porovnáš nabídky a vybereš ten správný parfém.");
      case CategoryId.Cars:
        return $gettext("V naší sekci auta najdeš širokou nabídku veteránů a sběratelských vozidel od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš klasické vozy, youngtimery nebo vzácné modely, u nás snadno porovnáš nabídky a vybereš to správné auto.");
      case CategoryId.Comics:
        return $gettext("V naší sekci komiksy najdeš širokou nabídku sběratelských komiksů od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš první vydání, vzácné série nebo graphic novels, u nás snadno porovnáš nabídky a vybereš ten správný komiks.");
      case CategoryId.Postcards:
        return $gettext("V naší sekci pohlednice najdeš širokou nabídku historických pohlednic od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš místopisné pohledy, umělecké pohlednice nebo tematické série, u nás snadno porovnáš nabídky a vybereš tu správnou pohlednici.");
      case CategoryId.Weapons:
        return $gettext("V naší sekci zbraně najdeš širokou nabídku historických a dekorativních zbraní od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš chladné zbraně, historické palné zbraně nebo repliky, u nás snadno porovnáš nabídky a vybereš tu správnou zbraň.");
      case CategoryId.MagicTheGathering:
        return $gettext("V naší sekci Magic: The Gathering najdeš širokou nabídku karet i sealed produktů od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš jednotlivé karty do decku nebo sběratelské boxy, u nás snadno porovnáš nabídky a vybereš ten správný MTG produkt.");
      case CategoryId.Pokemon:
        return $gettext("V naší sekci Pokémon TCG najdeš širokou nabídku karet i sealed produktů od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš jednotlivé karty do balíčku nebo sběratelské boxy, u nás snadno porovnáš nabídky a vybereš ten správný Pokémon produkt.");
      case CategoryId.SportsCards:
        return $gettext("V naší sekci sportovní karty najdeš širokou nabídku karet i sealed produktů od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš hokejové, fotbalové nebo basketbalové karty, u nás snadno porovnáš nabídky a vybereš tu správnou sportovní kartu.");
      case CategoryId.Books:
        return $gettext("V naší sekci knihy najdeš širokou nabídku sběratelských knih od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš první vydání, podepsané výtisky nebo vzácné svazky, u nás snadno porovnáš nabídky a vybereš tu správnou knihu.");
      case CategoryId.Handbags:
        return $gettext("V naší sekci kabelky najdeš širokou nabídku luxusních kabelek od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš vintage kousky, limitované edice nebo ikonické modely, u nás snadno porovnáš nabídky a vybereš tu správnou kabelku.");
      case CategoryId.Porcelain:
        return $gettext("V naší sekci porcelán najdeš širokou nabídku sběratelského porcelánu od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš starožitný porcelán, figurky nebo luxusní servis, u nás snadno porovnáš nabídky a vybereš ten správný porcelán.");
      case CategoryId.MoviePosters:
        return $gettext("V naší sekci filmové plakáty najdeš širokou nabídku sběratelských plakátů od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš originální plakáty, vintage tisky nebo limitované edice, u nás snadno porovnáš nabídky a vybereš ten správný plakát.");
      case CategoryId.BoardGames:
        return $gettext("V naší sekci deskové hry najdeš širokou nabídku sběratelských her od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš retro hry, limitované edice nebo vzácné tituly, u nás snadno porovnáš nabídky a vybereš tu správnou hru.");
      case CategoryId.Banknotes:
        return $gettext("V naší sekci bankovky najdeš širokou nabídku sběratelských bankovek od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš historické bankovky, pamětní emise nebo zahraniční měny, u nás snadno porovnáš nabídky a vybereš tu správnou bankovku.");
      case CategoryId.Phaleristics:
        return $gettext("V naší sekci faleristika najdeš širokou nabídku vyznamenání a medailí od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš historické řády, vojenské medaile nebo pamětní odznaky, u nás snadno porovnáš nabídky a vybereš ten správný kus.");
      case CategoryId.Other:
        return $gettext("V naší sekci ostatní najdeš širokou nabídku různých sběratelských předmětů od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš unikátní kousky nebo neobvyklé sběratelské položky, u nás snadno porovnáš nabídky a vybereš ten správný předmět.");
      case CategoryId.LegoMinifigures:
        return $gettext("V naší sekci LEGO minifigurky najdeš širokou nabídku figurek od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš vzácné minifigurky, limitované série nebo kompletní kolekce, u nás snadno porovnáš nabídky a vybereš tu správnou LEGO figurku.");
      case CategoryId.StarWarsCards:
        return $gettext("V naší sekci Star Wars karty najdeš širokou nabídku karet i sealed produktů od ověřených sběratelů a e-shopů přehledně na jednom místě. Ať už hledáš jednotlivé karty nebo sběratelské boxy, u nás snadno porovnáš nabídky a vybereš ten správný Star Wars produkt.");
      default:
        return undefined;
    }
  });

  const queryPlaceholder = computed(() => {
    switch (categoryId.value) {
      case CategoryId.Pokemon:
        return $gettext("Co hledáš? Např. Pikachu VMAX");
      case CategoryId.SportsCards:
        return $gettext("Co hledáš? Např. Jaromír Jágr rookie");
      case CategoryId.MagicTheGathering:
        return $gettext("Co hledáš? Např. Black Lotus");
      case CategoryId.LegoSets:
        return $gettext("Co hledáš? Např. Millennium Falcon 75192");
      case CategoryId.LegoMinifigures:
        return $gettext("Co hledáš? Např. Mr. Gold");
      case CategoryId.Watches:
        return $gettext("Co hledáš? Např. Omega Speedmaster");
      case CategoryId.Alcohol:
        return $gettext("Co hledáš? Např. Macallan 18");
      case CategoryId.Shoes:
        return $gettext("Co hledáš? Např. Air Jordan 1 Retro");
      case CategoryId.Art:
        return $gettext("Co hledáš? Např. Andy Warhol litografie");
      case CategoryId.Furniture:
        return $gettext("Co hledáš? Např. Thonet židle");
      case CategoryId.Jewellery:
        return $gettext("Co hledáš? Např. Cartier prsten");
      case CategoryId.Coins:
        return $gettext("Co hledáš? Např. 100 Kč zlatá mince");
      case CategoryId.ScaleModels:
        return $gettext("Co hledáš? Např. Ferrari 250 GTO 1:18");
      case CategoryId.Stamps:
        return $gettext("Co hledáš? Např. Hradčany 1918");
      case CategoryId.FunkoPop:
        return $gettext("Co hledáš? Např. Batman Chase");
      case CategoryId.VinylRecords:
        return $gettext("Co hledáš? Např. Pink Floyd The Wall");
      case CategoryId.MusicalInstruments:
        return $gettext("Co hledáš? Např. Fender Stratocaster");
      case CategoryId.Autographs:
        return $gettext("Co hledáš? Např. podpis Wayne Gretzky");
      case CategoryId.Perfumes:
        return $gettext("Co hledáš? Např. Chanel No. 5 vintage");
      case CategoryId.Cars:
        return $gettext("Co hledáš? Např. Škoda 110 R");
      case CategoryId.Comics:
        return $gettext("Co hledáš? Např. Čtyřlístek #1");
      case CategoryId.Postcards:
        return $gettext("Co hledáš? Např. Praha Staroměstské náměstí");
      case CategoryId.Weapons:
        return $gettext("Co hledáš? Např. šavle 19. století");
      case CategoryId.Books:
        return $gettext("Co hledáš? Např. Tolkien první vydání");
      case CategoryId.Handbags:
        return $gettext("Co hledáš? Např. Hermès Birkin");
      case CategoryId.Porcelain:
        return $gettext("Co hledáš? Např. Meissen figurka");
      case CategoryId.MoviePosters:
        return $gettext("Co hledáš? Např. Star Wars originál plakát");
      case CategoryId.BoardGames:
        return $gettext("Co hledáš? Např. Dostihy a sázky retro");
      case CategoryId.Banknotes:
        return $gettext("Co hledáš? Např. 5000 Kč 1920");
      case CategoryId.Phaleristics:
        return $gettext("Co hledáš? Např. Řád bílého lva");
      case CategoryId.StarWarsCards:
        return $gettext("Co hledáš? Např. Darth Vader holografická");
      case CategoryId.Other:
        return $gettext("Co hledáš?");
      default:
        return undefined;
    }
  });

  const title = computed(() => {
    const id = categoryId.value;
    let title = "";

    if (id === CategoryId.LegoMinifigures) {
      title = $gettext("Nejlepší Marketplace LEGO figurek");
    } else if (id === CategoryId.LegoSets) {
      title = $gettext("LEGO sety na Marketplace");
    } else if (id === CategoryId.Watches) {
      title = $gettext("Prodej hodinek na tržišti");
    } else if (id === CategoryId.Shoes) {
      title = $gettext("Marketplace s teniskami");
    } else if (id === CategoryId.VinylRecords) {
      title = $gettext("Skvělé ceny vinylů");
    } else if (id === CategoryId.BoardGames) {
      title = $gettext("Deskové hry na tržišti čekají na tebe");
    } else if (id === CategoryId.Alcohol) {
      title = $gettext("Prodej raritního alkoholu");
    } else if (id === CategoryId.Autographs) {
      title = $gettext("Vyber si autogram na Marketplace");
    } else if (id === CategoryId.Cars) {
      title = $gettext("Nejlepší modely aut na tržišti");
    } else if (id === CategoryId.Banknotes) {
      title = $gettext("Široký výběr starých bankovek na tržišti");
    } else if (id === CategoryId.MoviePosters) {
      title = $gettext("Plakáty pro sběratele na tržišti");
    } else if (id === CategoryId.FunkoPop) {
      title = $gettext("Široký výběr FunkoPOP figurek na tržišti");
    } else if (id === CategoryId.MusicalInstruments) {
      title = $gettext("Unikátní hudební nástroje na Marketplace");
    } else if (id === CategoryId.Handbags) {
      title = $gettext("Široký výběr kabelek na tržišti");
    } else if (id === CategoryId.Books) {
      title = $gettext("Knihy pro sběratele i fanoušky");
    } else if (id === CategoryId.Comics) {
      title = $gettext("Výběr komiksů pro sběratele");
    } else if (id === CategoryId.MagicTheGathering) {
      title = $gettext("Magic The Gathering pro sběratele");
    } else if (id === CategoryId.Coins) {
      title = $gettext("Unikátní mince na tržišti");
    } else if (id === CategoryId.ScaleModels) {
      title = $gettext("Tržiště pro fanoušky modelářství");
    } else if (id === CategoryId.Furniture) {
      title = $gettext("Prodej starožitného nábytku");
    } else if (id === CategoryId.Perfumes) {
      title = $gettext("Luxusní parfémy na tržišti");
    } else if (id === CategoryId.Pokemon) {
      title = $gettext("Pokémon karty na prodej");
    } else if (id === CategoryId.Porcelain) {
      title = $gettext("Exkluzivní nabídky porcelánu");
    } else if (id === CategoryId.SportsCards) {
      title = $gettext("Široký výběr sportovních karet na tržišti");
    } else if (id === CategoryId.Art) {
      title = $gettext("Unikátní umělecká díla na Marketplace");
    } else if (id === CategoryId.StarWarsCards) {
      title = $gettext("Exkluzivní nabídky Star Wars předmětů");
    } else if (id === CategoryId.Weapons) {
      title = $gettext("Historické zbraně k prodeji");
    } else if (id === CategoryId.Jewellery) {
      title = $gettext("Nejlepší nabídky šperků na tržišti");
    } else if (id === CategoryId.Postcards) {
      title = $gettext("Pohlednice pro sběratele na tržišti");
    } else if (id === CategoryId.Stamps) {
      title = $gettext("Poštovní známky na Marketplace");
    } else {
      title = $gettext("Sběratelské kousky na prodej");
    }

    return createPageTitle(title);
  });

  return {
    shortDescription,
    description,
    queryPlaceholder,
    title,
  };
}
